import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { SortableHeader } from "@/components/ui/sortable-header";
import { useSort } from "@/components/ui/sortable";
import { formatNumber, fromNowFR } from "@/lib/utils";

type ThreadSortKey =
  | "title"
  | "replies_count"
  | "views"
  | "last_post_at"
  | "first_seen_at";

interface Props {
  forumId: number | string;
}

export function ForumThreadsTable({ forumId }: Props) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["threads", forumId],
    queryFn: () => api.threads(forumId),
    enabled: forumId != null,
  });

  const { state, toggle, sort } = useSort<ThreadSortKey, any>("last_post_at", "desc");
  const sorted = useMemo(
    () =>
      sort(data, {
        title: (t) => t.title,
        replies_count: (t) => t.replies_count,
        views: (t) => t.views,
        last_post_at: (t) => t.last_post_at,
        first_seen_at: (t) => t.first_seen_at,
      }),
    [data, sort]
  );

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-foreground-muted text-sm">
          Aucun thread pour ce forum.
          <br />
          Cliquez sur « Threads » dans la barre d'actions pour déclencher un scrape immédiat.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader
              active={state.key === "title"}
              dir={state.dir}
              onSort={() => toggle("title")}
            >
              Titre
            </SortableHeader>
            <SortableHeader
              className="text-right"
              active={state.key === "replies_count"}
              dir={state.dir}
              onSort={() => toggle("replies_count")}
            >
              Réponses
            </SortableHeader>
            <SortableHeader
              className="text-right"
              active={state.key === "views"}
              dir={state.dir}
              onSort={() => toggle("views")}
            >
              Vues
            </SortableHeader>
            <SortableHeader
              active={state.key === "last_post_at"}
              dir={state.dir}
              onSort={() => toggle("last_post_at")}
            >
              Dernier post
            </SortableHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((t: any) => (
            <TableRow key={t.id}>
              <TableCell>
                <Link to={`/threads/${t.id}`} className="font-medium hover:underline">
                  {t.title}
                </Link>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatNumber(t.replies_count)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatNumber(t.views)}
              </TableCell>
              <TableCell className="text-foreground-muted text-xs">
                {fromNowFR(t.last_post_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
